import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  Plus,
  Mail,
  Phone,
  Search,
  Trash2,
  Edit3,
  X,
  Check,
  ExternalLink,
  RotateCcw,
  MapPin,
  Building2,
  MessageCircle,
  Clock,
  Coins,
  ArrowRight,
  Smartphone,
  FolderKanban,
  FileText,
  User,
  Megaphone,
  Gift,
  Copy,
  Receipt,
  ShieldCheck,
  KeyRound,
  Eye,
  EyeOff,
} from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { useToast } from '@/context/ToastContext.js';
import {
  ALL_HOLIDAYS,
  PROMOTION_TEMPLATES,
  calculateCountdown,
  interpolateTemplate,
  buildEmailLink,
  buildWhatsAppLink,
} from '@/lib/promotionData.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { PullToRefresh } from '@/components/ui/PullToRefresh.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { SkeletonCard } from '@/components/ui/skeleton.js';
import { Client, ClientStatus, PlatformType, Invoice } from '@/types/index.js';
import { PlatformBadge } from '@/components/ui/PlatformBadge.js';
import { formatExternalUrl } from '@/lib/utils.js';

export {
  COMMON_COUNTRIES,
  COMMON_TIMEZONES,
  COMMON_CURRENCIES,
  getCountryFlag,
  cleanPhoneForWhatsApp,
  cleanPhoneForTel,
  resolveClientPlatformUrl,
} from '@/lib/clientUtils.js';

import {
  COMMON_COUNTRIES,
  COMMON_TIMEZONES,
  COMMON_CURRENCIES,
  getCountryFlag,
  cleanPhoneForWhatsApp,
  cleanPhoneForTel,
  resolveClientPlatformUrl,
} from '@/lib/clientUtils.js';

const emptyClientForm = {
  name: '',
  company: '',
  country: '',
  platform: 'direct' as PlatformType,
  platform_username: '',
  platform_url: '',
  email: '',
  secondary_email: '',
  phone: '',
  mobile: '',
  address: '',
  timezone: '',
  currency: 'USD',
  notes: '',
  status: 'active' as ClientStatus,
};

export function ClientsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const canManage = isOwner || user?.role === 'manager';

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [platformTab, setPlatformTab] = useState<'all' | 'fiverr' | 'upwork' | 'direct' | 'internal' | 'other'>('all');
  const [platformFilter, setPlatformFilter] = useState('');
  const [portfolioFilter, setPortfolioFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Client Details Drawer State
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [drawerClientData, setDrawerClientData] = useState<Client | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerInvoices, setDrawerInvoices] = useState<Invoice[]>([]);
  const [drawerInvoicesLoading, setDrawerInvoicesLoading] = useState(false);
  const toast = useToast();
  const [drawerHolidayId, setDrawerHolidayId] = useState<string>('hol_usa_black_friday');
  const [drawerCopied, setDrawerCopied] = useState<boolean>(false);

  // Create Modal State
  const [showModal, setShowModal] = useState(false);
  const [newClient, setNewClient] = useState(emptyClientForm);

  // Edit Modal State
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editForm, setEditForm] = useState(emptyClientForm);

  // Delete Confirmation State
  const [deleteClientTarget, setDeleteClientTarget] = useState<{ id: string; name: string } | null>(null);

  // Toast feedback
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Portal Access & Onboarding Hub State
  const [portalModalClient, setPortalModalClient] = useState<Client | null>(null);
  const [portalGenerating, setPortalGenerating] = useState(false);
  const [portalCustomEmail, setPortalCustomEmail] = useState('');
  const [portalCustomPassword, setPortalCustomPassword] = useState('');
  const [portalAccessData, setPortalAccessData] = useState<{
    portal_token: string;
    portal_url: string;
    password_display: string;
    expires_at: string;
    client_email?: string;
  } | null>(null);
  const [portalCopiedField, setPortalCopiedField] = useState<string | null>(null);
  const [showPortalPassword, setShowPortalPassword] = useState(false);

  const handleOpenPortalModal = (client: Client) => {
    setPortalModalClient(client);
    setPortalCustomEmail(client.email || '');
    setPortalCustomPassword('');
    setShowPortalPassword(false);
    setPortalCopiedField(null);

    if (client.portal_token) {
      setPortalAccessData({
        portal_token: client.portal_token,
        portal_url: `#/portal?token=${client.portal_token}`,
        password_display: client.portal_password || 'Client!7c4a#26',
        expires_at: client.portal_token_expires_at || '',
        client_email: client.email || '',
      });
    } else {
      setPortalAccessData(null);
    }
  };

  const handleGeneratePortalAccess = async () => {
    if (!portalModalClient) return;
    setPortalGenerating(true);
    try {
      const res = await api.post<any>(`/api/v1/clients/${portalModalClient.id}/portal-access`, {
        email: portalCustomEmail.trim() || undefined,
        password: portalCustomPassword.trim() || undefined,
      });
      const data = res.data || res;
      setPortalAccessData(data);
      toast.success('Client onboarding credentials generated successfully! 🔐');
      fetchClients();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate portal credentials');
    } finally {
      setPortalGenerating(false);
    }
  };

  // Close modals & drawer on Escape key
  useEffect(() => {
    if (!editingClient && !showModal && !selectedClient) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editingClient) setEditingClient(null);
        else if (showModal) setShowModal(false);
        else if (selectedClient) handleCloseDrawer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingClient, showModal, selectedClient]);

  const fetchClients = async () => {
    try {
      const res = await api.get<{ data: Client[] }>('/api/v1/clients');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setClients(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleOpenDrawer = async (c: Client) => {
    setSelectedClient(c);
    setDrawerClientData(c);
    setDrawerLoading(true);
    setDrawerCopied(false);
    const upcoming = ALL_HOLIDAYS.find((h) => {
      const cd = calculateCountdown(h.date);
      return cd.daysUntil >= 0;
    });
    if (upcoming) {
      setDrawerHolidayId(upcoming.id);
    }

    if (isOwner) {
      setDrawerInvoicesLoading(true);
      api.get<{ data: Invoice[] } | Invoice[]>(`/api/invoices?client_id=${encodeURIComponent(c.id)}`)
        .then((res: any) => {
          const list = res?.data || (Array.isArray(res) ? res : []);
          setDrawerInvoices(list);
        })
        .catch((err) => {
          console.error('Failed to load client invoices:', err);
          setDrawerInvoices([]);
        })
        .finally(() => setDrawerInvoicesLoading(false));
    }

    try {
      const res = await api.get<{ data: Client }>(`/api/v1/clients/${c.id}`);
      if (res?.data) {
        setDrawerClientData(res.data);
      }
    } catch (err) {
      console.error('Failed to load enriched client details:', err);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setSelectedClient(null);
    setDrawerClientData(null);
    setDrawerInvoices([]);
  };

  const handleCreateClient = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/clients', {
        name: newClient.name.trim(),
        company: newClient.company.trim() || null,
        country: newClient.country.trim() || null,
        platform: newClient.platform || 'direct',
        platform_username: newClient.platform_username.trim() || null,
        platform_url: formatExternalUrl(newClient.platform_url) || null,
        email: newClient.email.trim(),
        secondary_email: newClient.secondary_email.trim() || null,
        phone: newClient.phone.trim() || null,
        mobile: newClient.mobile.trim() || null,
        address: newClient.address.trim() || null,
        timezone: newClient.timezone.trim() || null,
        currency: newClient.currency.trim() || 'USD',
        notes: newClient.notes.trim() || null,
        status: newClient.status || 'active',
      });
      setShowModal(false);
      setNewClient(emptyClientForm);
      showToast('Client created successfully!');
      fetchClients();
    } catch (e: any) {
      alert(e.message || 'Failed to create client');
    }
  };

  const handleOpenEdit = (c: Client) => {
    setEditingClient(c);
    setEditForm({
      name: c.name || '',
      company: c.company || '',
      country: c.country || '',
      platform: (c.platform || 'direct') as PlatformType,
      platform_username: c.platform_username || '',
      platform_url: c.platform_url || '',
      email: c.email || '',
      secondary_email: c.secondary_email || '',
      phone: c.phone || '',
      mobile: c.mobile || '',
      address: c.address || '',
      timezone: c.timezone || '',
      currency: c.currency || 'USD',
      notes: c.notes || '',
      status: (c.status || 'active') as ClientStatus,
    });
  };

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingClient) return;
    try {
      const payload = {
        name: editForm.name.trim(),
        company: editForm.company.trim() || null,
        country: editForm.country.trim() || null,
        platform: editForm.platform || 'direct',
        platform_username: editForm.platform_username.trim() || null,
        platform_url: formatExternalUrl(editForm.platform_url) || null,
        email: editForm.email.trim(),
        secondary_email: editForm.secondary_email.trim() || null,
        phone: editForm.phone.trim() || null,
        mobile: editForm.mobile.trim() || null,
        address: editForm.address.trim() || null,
        timezone: editForm.timezone.trim() || null,
        currency: editForm.currency.trim() || 'USD',
        notes: editForm.notes.trim() || null,
        status: editForm.status || 'active',
      };
      const res = await api.patch<{ data: Client }>(`/api/v1/clients/${editingClient.id}`, payload);
      const updated = res?.data || { ...editingClient, ...payload };

      setEditingClient(null);
      showToast('Client updated successfully!');
      fetchClients();

      // If drawer is currently showing this client, update drawer view
      if (selectedClient && selectedClient.id === editingClient.id) {
        setSelectedClient((prev) => (prev ? { ...prev, ...updated } : updated));
        setDrawerClientData((prev) => (prev ? { ...prev, ...updated, projects: updated.projects ?? prev.projects } : updated));
      }
    } catch (e: any) {
      alert(e.message || 'Failed to update client');
    }
  };

  const handleQuickPlatformChange = async (client: Client, newPlatform: PlatformType) => {
    try {
      const res = await api.patch<{ data: Client }>(`/api/v1/clients/${client.id}`, { platform: newPlatform });
      const updated = res?.data || { ...client, platform: newPlatform };
      setClients((prev) => prev.map((item) => (item.id === client.id ? { ...item, ...updated } : item)));
      if (selectedClient && selectedClient.id === client.id) {
        setSelectedClient((prev) => (prev ? { ...prev, ...updated } : null));
      }
      if (drawerClientData && drawerClientData.id === client.id) {
        setDrawerClientData((prev) => (prev ? { ...prev, ...updated } : null));
      }
      showToast(`Client platform updated to ${newPlatform.toUpperCase()}`);
      fetchClients();
    } catch (err: any) {
      alert(err.message || 'Failed to update client platform');
    }
  };

  const handleDeleteClient = (id: string, name: string) => {
    setDeleteClientTarget({ id, name });
  };

  const confirmDeleteClient = async () => {
    if (!deleteClientTarget) return;
    const { id, name } = deleteClientTarget;
    try {
      await api.delete(`/api/v1/clients/${id}`);
      setClients((prev) => prev.filter((c) => c.id !== id));
      setDeleteClientTarget(null);
      if (selectedClient?.id === id) {
        handleCloseDrawer();
      }
      showToast(`Client "${name}" deleted.`);
      fetchClients();
    } catch (e: any) {
      alert(e.message || 'Failed to delete client');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const channelCounts = {
    all: clients.length,
    fiverr: clients.filter((c) => (c.platform || 'direct').toLowerCase() === 'fiverr').length,
    upwork: clients.filter((c) => (c.platform || 'direct').toLowerCase() === 'upwork').length,
    direct: clients.filter((c) => (c.platform || 'direct').toLowerCase() === 'direct').length,
    internal: clients.filter((c) => (c.platform || 'direct').toLowerCase() === 'internal').length,
    other: clients.filter((c) => (c.platform || 'direct').toLowerCase() === 'other').length,
  };

  const hasActiveFilters =
    platformTab !== 'all' ||
    Boolean(platformFilter) ||
    Boolean(portfolioFilter) ||
    Boolean(statusFilter) ||
    Boolean(search);

  const handleResetFilters = () => {
    setPlatformTab('all');
    setPlatformFilter('');
    setPortfolioFilter('');
    setStatusFilter('');
    setSearch('');
    setCurrentPage(1);
  };

  const filtered = clients.filter((c) => {
    const pPlatform = (c.platform || 'direct').toLowerCase();
    const matchesTab = platformTab === 'all' || pPlatform === platformTab;
    const matchesPlatformFilter = !platformFilter || pPlatform === platformFilter.toLowerCase();
    const cStatus = (c.status || 'active').toLowerCase();
    const matchesStatusFilter = !statusFilter || cStatus === statusFilter.toLowerCase();

    const q = search.toLowerCase();
    const matchesSearch = q
      ? (c.name && c.name.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.secondary_email && c.secondary_email.toLowerCase().includes(q)) ||
        (c.company && c.company.toLowerCase().includes(q)) ||
        (c.country && c.country.toLowerCase().includes(q)) ||
        (c.phone && c.phone.toLowerCase().includes(q)) ||
        (c.mobile && c.mobile.toLowerCase().includes(q)) ||
        (c.platform_username && c.platform_username.toLowerCase().includes(q)) ||
        (c.address && c.address.toLowerCase().includes(q))
      : true;

    const projectCount = c.active_projects_count ?? c.projects_count ?? 0;
    const matchesPortfolio =
      portfolioFilter === 'has_projects'
        ? projectCount > 0
        : portfolioFilter === 'no_projects'
        ? projectCount === 0
        : true;

    return matchesTab && matchesPlatformFilter && matchesStatusFilter && matchesSearch && matchesPortfolio;
  });

  const sorted = [...filtered].sort((a: any, b: any) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const pageSize = 24;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((validCurrentPage - 1) * pageSize, validCurrentPage * pageSize);

  const activeDrawer = drawerClientData || selectedClient;
  const activeDrawerWhatsApp = cleanPhoneForWhatsApp(activeDrawer?.mobile || activeDrawer?.phone);
  const activeDrawerTel = cleanPhoneForTel(activeDrawer?.phone || activeDrawer?.mobile);
  const activeDrawerPlatformUrl = resolveClientPlatformUrl(activeDrawer);

  const activeDrawerHoliday = ALL_HOLIDAYS.find((h) => h.id === drawerHolidayId) || ALL_HOLIDAYS[0];
  const activeDrawerTemplate =
    PROMOTION_TEMPLATES.find((t) => t.holiday_id === drawerHolidayId) ||
    PROMOTION_TEMPLATES.find((t) => t.category === activeDrawerHoliday.category) ||
    PROMOTION_TEMPLATES[0];
  const activeDrawerCountdown = calculateCountdown(activeDrawerHoliday.date);
  const activeDrawerInterpolated = activeDrawer
    ? interpolateTemplate(
        { subject: activeDrawerTemplate.subject, body: activeDrawerTemplate.body },
        {
          clientName: activeDrawer.name,
          companyName: activeDrawer.company || activeDrawer.name,
          discountPercent: activeDrawerTemplate.suggested_discount || '25%',
          deadline: activeDrawerHoliday.date,
          senderName: user?.name || 'Techosolution Team',
        }
      )
    : null;

  const handleDrawerCopyPitch = async () => {
    if (!activeDrawerInterpolated) return;
    try {
      await navigator.clipboard.writeText(
        `${activeDrawerInterpolated.subject}\n\n${activeDrawerInterpolated.body}`
      );
      setDrawerCopied(true);
      toast.success('Promotional pitch copied to clipboard!');
      setTimeout(() => setDrawerCopied(false), 2500);
    } catch {
      toast.error('Failed to copy to clipboard.');
    }
  };

  return (
    <PullToRefresh onRefresh={fetchClients}>
      <div className="space-y-6">
        {/* Toast Feedback */}
        {toastMessage && (
          <div className="fixed top-16 right-6 z-50 bg-brand-navy text-white px-4 py-2.5 rounded-lg shadow-xl text-xs flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
            <Check className="h-4 w-4 text-brand-teal" />
            <span>{toastMessage}</span>
          </div>
        )}

        {/* Datalist for Country Autocomplete */}
        <datalist id="common-countries">
          {COMMON_COUNTRIES.map((cty) => (
            <option key={cty} value={cty} />
          ))}
        </datalist>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Clients Directory</h1>
            <p className="helper-text mt-1">
              Keep full records of clients, platforms, contacts, locations, and linked agency projects.
            </p>
          </div>
          {canManage && (
            <Button
              onClick={() => setShowModal(true)}
              className="flex items-center space-x-1.5 font-semibold bg-brand-teal text-white hover:bg-brand-teal-action min-h-[44px] text-xs shadow-xs"
            >
              <Plus className="h-4 w-4" />
              <span>New Client</span>
            </Button>
          )}
        </div>

        {/* Channel / Platform Tabs */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-1 border-b border-slate-200 dark:border-brand-border flex-nowrap scrollbar-none">
          <button
            type="button"
            onClick={() => {
              setPlatformTab('all');
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-md transition-colors flex items-center space-x-1.5 border-b-2 shrink-0 ${
              platformTab === 'all'
                ? 'border-brand-teal text-brand-teal bg-teal-50/50 dark:bg-brand-teal/10'
                : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400'
            }`}
          >
            <span>All Channels</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-200 dark:bg-slate-700 font-mono">
              {channelCounts.all}
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setPlatformTab('fiverr');
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-md transition-colors flex items-center space-x-1.5 border-b-2 shrink-0 ${
              platformTab === 'fiverr'
                ? 'border-[#1dbf73] text-[#0f8c51] dark:text-[#22c55e] bg-[#1dbf73]/10'
                : 'border-transparent text-slate-600 hover:text-[#0f8c51] dark:text-slate-400'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-[#1dbf73]" />
            <span>Fiverr</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-emerald-100 text-[#0f8c51] dark:bg-emerald-950/60 dark:text-emerald-300 font-mono">
              {channelCounts.fiverr}
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setPlatformTab('upwork');
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-md transition-colors flex items-center space-x-1.5 border-b-2 shrink-0 ${
              platformTab === 'upwork'
                ? 'border-[#14a800] text-[#0f8200] dark:text-[#4ade80] bg-[#14a800]/10'
                : 'border-transparent text-slate-600 hover:text-[#0f8200] dark:text-slate-400'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-[#14a800]" />
            <span>Upwork</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-emerald-100 text-[#0f8200] dark:bg-emerald-950/60 dark:text-emerald-300 font-mono">
              {channelCounts.upwork}
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setPlatformTab('direct');
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-md transition-colors flex items-center space-x-1.5 border-b-2 shrink-0 ${
              platformTab === 'direct'
                ? 'border-indigo-600 text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40'
                : 'border-transparent text-slate-600 hover:text-indigo-600 dark:text-slate-400'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-indigo-600" />
            <span>Direct Client</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 font-mono">
              {channelCounts.direct}
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setPlatformTab('internal');
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-md transition-colors flex items-center space-x-1.5 border-b-2 shrink-0 ${
              platformTab === 'internal'
                ? 'border-purple-600 text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/40'
                : 'border-transparent text-slate-600 hover:text-purple-600 dark:text-slate-400'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-purple-600" />
            <span>Internal</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 font-mono">
              {channelCounts.internal}
            </span>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white dark:bg-[#12211F] p-3 rounded-lg border border-slate-200 dark:border-brand-border">
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64 min-w-[180px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search name, handle, country, phone..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-9 h-9 text-xs"
              />
            </div>
            <select
              value={platformFilter}
              onChange={(e) => {
                setPlatformFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
            >
              <option value="">All Platforms</option>
              <option value="fiverr">Fiverr</option>
              <option value="upwork">Upwork</option>
              <option value="direct">Direct Client</option>
              <option value="internal">Internal</option>
              <option value="other">Other</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="lead">Lead</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={portfolioFilter}
              onChange={(e) => {
                setPortfolioFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
            >
              <option value="">All Portfolios</option>
              <option value="has_projects">With Active Projects</option>
              <option value="no_projects">No Active Projects</option>
            </select>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="h-9 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-white flex items-center space-x-1"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                <span>Reset</span>
              </Button>
            )}
          </div>
          <div className="flex items-center space-x-2 text-xs text-slate-500 overflow-x-auto pb-1 sm:pb-0">
            <span className="font-medium text-slate-400 shrink-0">Sort:</span>
            <SortButton label="Name" field="name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
            <SortButton label="Company" field="company" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
            <SortButton label="Country" field="country" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
            <SortButton label="Projects" field="active_projects_count" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          </div>
        </div>

        {/* Clients List / Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-slate-500 space-y-2">
              <p>No clients found matching current criteria.</p>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={handleResetFilters} className="text-xs">
                  Reset All Filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginated.map((c) => {
                const flag = getCountryFlag(c.country);
                const hasWhatsApp = cleanPhoneForWhatsApp(c.mobile || c.phone);
                const hasTel = cleanPhoneForTel(c.mobile || c.phone);
                const statusNorm = (c.status || 'active').toLowerCase();

                return (
                  <Card
                    key={c.id}
                    onClick={() => handleOpenDrawer(c)}
                    className="hover:border-brand-teal/50 dark:hover:border-brand-teal/50 transition-all flex flex-col justify-between bg-white dark:bg-[#12211F] border-slate-200 dark:border-brand-border shadow-xs hover:shadow cursor-pointer group"
                  >
                    <CardContent className="p-4 sm:p-5 space-y-3">
                      {/* Top Badges & Actions */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center space-x-1.5 flex-wrap gap-y-1">
                          <PlatformBadge platform={c.platform} size="xs" />
                          {c.platform_username && (
                            <span className="text-[11px] font-mono text-slate-500 dark:text-slate-400">
                              @{c.platform_username}
                            </span>
                          )}
                          {resolveClientPlatformUrl(c) && (
                            <a
                              href={resolveClientPlatformUrl(c)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-slate-400 hover:text-brand-teal p-0.5 inline-flex items-center"
                              title="Open external profile/workroom"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <div className="flex items-center space-x-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium border uppercase tracking-wider ${
                              statusNorm === 'active'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                                : statusNorm === 'lead'
                                ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800'
                                : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700'
                            }`}
                          >
                            {statusNorm}
                          </span>
                          {canManage && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/promotions?client_id=${c.id}&tab=composer`);
                                }}
                                className="h-7 w-7 text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-950/40"
                                title="Send Holiday Promotion / Pitch"
                              >
                                <Megaphone className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPortalModal(c);
                                }}
                                className="h-7 w-7 text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-950/40"
                                title="Client Onboarding & Portal Hub"
                              >
                                <ShieldCheck className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenEdit(c);
                                }}
                                className="h-7 w-7 text-slate-400 hover:text-brand-navy dark:hover:text-white"
                                title="Edit Client"
                              >
                                <Edit3 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          {isOwner && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClient(c.id, c.name);
                              }}
                              className="h-7 w-7 text-slate-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                              title="Delete Client"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Client Name & Company */}
                      <div>
                        <h3 className="font-semibold text-base text-brand-navy dark:text-brand-ink group-hover:text-brand-teal transition-colors flex items-center justify-between">
                          <span>{c.name}</span>
                          <span className="text-xs text-slate-400 font-normal group-hover:translate-x-0.5 transition-transform">
                            →
                          </span>
                        </h3>
                        {c.company && (
                          <p className="text-xs font-medium text-slate-500 dark:text-brand-muted flex items-center space-x-1 mt-0.5">
                            <Building2 className="w-3 h-3 text-slate-400 shrink-0" />
                            <span>{c.company}</span>
                          </p>
                        )}
                      </div>

                      {/* Country & Active Projects Counters */}
                      <div className="flex items-center justify-between pt-1 text-xs">
                        {c.country ? (
                          <span className="inline-flex items-center space-x-1 text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-brand-surface px-2 py-0.5 rounded border border-slate-200/60 dark:border-brand-border text-[11px]">
                            <span className="text-sm leading-none">{flag}</span>
                            <span>{c.country}</span>
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-400">Location not set</span>
                        )}
                        <Badge variant="secondary" className="text-[10px] font-medium font-mono">
                          {c.active_projects_count ?? c.projects_count ?? 0} active{' '}
                          {(c.active_projects_count ?? c.projects_count ?? 0) === 1 ? 'project' : 'projects'}
                        </Badge>
                      </div>

                      {/* Contact Details Row */}
                      <div
                        className="space-y-1 pt-2 border-t border-slate-100 dark:border-brand-border/60 text-xs text-slate-600 dark:text-slate-300"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 truncate">
                            <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            <a
                              href={`mailto:${c.email}`}
                              className="truncate hover:text-brand-teal transition-colors"
                              title={`Email: ${c.email}`}
                            >
                              {c.email || 'No email'}
                            </a>
                          </div>
                        </div>

                        {(c.mobile || c.phone) && (
                          <div className="flex items-center justify-between pt-0.5">
                            <div className="flex items-center space-x-2 truncate">
                              <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              <span className="truncate">{c.mobile || c.phone}</span>
                            </div>
                            <div className="flex items-center space-x-1.5 shrink-0">
                              {hasWhatsApp && (
                                <a
                                  href={`https://wa.me/${hasWhatsApp}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 inline-flex items-center p-1 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 rounded transition-colors"
                                  title="Chat on WhatsApp"
                                >
                                  <MessageCircle className="w-3.5 h-3.5" />
                                </a>
                              )}
                              {hasTel && (
                                <a
                                  href={`tel:${hasTel}`}
                                  className="text-slate-500 hover:text-brand-teal inline-flex items-center p-1 hover:bg-slate-100 dark:hover:bg-brand-surface rounded transition-colors"
                                  title="Call Phone"
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <Pagination
              totalItems={sorted.length}
              currentPage={validCurrentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* =========================================================================
            SLIDE-OVER DRAWER: CLIENT FULL PROFILE
           ========================================================================= */}
        {selectedClient && (
          <div
            className="fixed inset-0 z-50 overflow-hidden bg-slate-900/50 backdrop-blur-xs transition-opacity animate-in fade-in"
            onClick={handleCloseDrawer}
          >
            <div className="fixed inset-y-0 right-0 max-w-xl w-full flex pl-10">
              <div
                className="w-full bg-white dark:bg-[#12211F] shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-250 border-l border-slate-200 dark:border-brand-border"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Drawer Header */}
                <div className="p-6 border-b border-slate-200 dark:border-brand-border bg-slate-50/70 dark:bg-brand-surface/40">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                        <div className="flex items-center space-x-1.5 bg-white dark:bg-[#152724] border border-slate-200 dark:border-brand-border rounded-md px-2 py-0.5 shadow-xs">
                          <PlatformBadge platform={activeDrawer?.platform} size="xs" />
                          <select
                            value={(activeDrawer?.platform || 'direct').toLowerCase()}
                            onChange={(e) => handleQuickPlatformChange(activeDrawer!, e.target.value as PlatformType)}
                            className="text-[11px] font-semibold bg-transparent border-none p-0 text-slate-700 dark:text-slate-200 cursor-pointer focus:outline-none"
                            title="Change client platform channel"
                          >
                            <option value="direct">Direct</option>
                            <option value="upwork">Upwork</option>
                            <option value="fiverr">Fiverr</option>
                            <option value="internal">Internal</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        {activeDrawer?.country && (
                          <span className="inline-flex items-center space-x-1 text-xs font-medium px-2 py-0.5 rounded-full bg-white dark:bg-[#152724] border border-slate-200 dark:border-brand-border text-slate-700 dark:text-slate-200">
                            <span>{getCountryFlag(activeDrawer.country)}</span>
                            <span>{activeDrawer.country}</span>
                          </span>
                        )}
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider border ${
                            (activeDrawer?.status || 'active').toLowerCase() === 'active'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                              : (activeDrawer?.status || 'active').toLowerCase() === 'lead'
                              ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800'
                              : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/60 dark:text-slate-400'
                          }`}
                        >
                          {activeDrawer?.status || 'active'}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold text-brand-navy dark:text-brand-ink pt-1">
                        {activeDrawer?.name}
                      </h2>
                      {activeDrawer?.company && (
                        <p className="text-sm font-medium text-slate-600 dark:text-brand-muted flex items-center space-x-1.5">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span>{activeDrawer.company}</span>
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleCloseDrawer}
                      className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                      title="Close Drawer"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Quick Action Buttons */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4 pt-4 border-t border-slate-200/80 dark:border-brand-border/60">
                    <a
                      href={activeDrawer?.email ? `mailto:${activeDrawer.email}` : undefined}
                      className={`inline-flex items-center justify-center space-x-1.5 px-3 py-2 text-xs font-semibold rounded-md border transition-colors ${
                        activeDrawer?.email
                          ? 'bg-white hover:bg-slate-50 dark:bg-[#152724] dark:hover:bg-brand-surface text-slate-700 dark:text-slate-200 border-slate-200 dark:border-brand-border shadow-xs'
                          : 'opacity-40 pointer-events-none bg-slate-100 dark:bg-slate-800 border-transparent text-slate-400'
                      }`}
                      title={activeDrawer?.email ? `Send email to ${activeDrawer.email}` : 'No email address'}
                    >
                      <Mail className="w-3.5 h-3.5 text-blue-500" />
                      <span>Email</span>
                    </a>

                    <a
                      href={activeDrawerWhatsApp ? `https://wa.me/${activeDrawerWhatsApp}` : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center justify-center space-x-1.5 px-3 py-2 text-xs font-semibold rounded-md border transition-colors ${
                        activeDrawerWhatsApp
                          ? 'bg-white hover:bg-emerald-50/50 dark:bg-[#152724] dark:hover:bg-emerald-950/20 text-[#0f8c51] dark:text-[#22c55e] border-emerald-200 dark:border-emerald-800/60 shadow-xs'
                          : 'opacity-40 pointer-events-none bg-slate-100 dark:bg-slate-800 border-transparent text-slate-400'
                      }`}
                      title={activeDrawerWhatsApp ? 'Chat on WhatsApp' : 'No mobile number'}
                    >
                      <MessageCircle className="w-3.5 h-3.5 text-emerald-500" />
                      <span>WhatsApp</span>
                    </a>

                    <a
                      href={activeDrawerTel ? `tel:${activeDrawerTel}` : undefined}
                      className={`inline-flex items-center justify-center space-x-1.5 px-3 py-2 text-xs font-semibold rounded-md border transition-colors ${
                        activeDrawerTel
                          ? 'bg-white hover:bg-slate-50 dark:bg-[#152724] dark:hover:bg-brand-surface text-slate-700 dark:text-slate-200 border-slate-200 dark:border-brand-border shadow-xs'
                          : 'opacity-40 pointer-events-none bg-slate-100 dark:bg-slate-800 border-transparent text-slate-400'
                      }`}
                      title={activeDrawerTel ? 'Call client phone' : 'No phone number'}
                    >
                      <Phone className="w-3.5 h-3.5 text-indigo-500" />
                      <span>Call</span>
                    </a>

                    <a
                      href={activeDrawerPlatformUrl || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center justify-center space-x-1.5 px-3 py-2 text-xs font-semibold rounded-md border transition-colors ${
                        activeDrawerPlatformUrl
                          ? 'bg-white hover:bg-slate-50 dark:bg-[#152724] dark:hover:bg-brand-surface text-slate-700 dark:text-slate-200 border-slate-200 dark:border-brand-border shadow-xs'
                          : 'opacity-40 pointer-events-none bg-slate-100 dark:bg-slate-800 border-transparent text-slate-400'
                      }`}
                      title={activeDrawerPlatformUrl ? `Open platform profile (${activeDrawer?.platform || 'direct'})` : 'No platform profile link'}
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-purple-500" />
                      <span>Platform</span>
                    </a>

                    <button
                      type="button"
                      onClick={() => {
                        handleCloseDrawer();
                        navigate(`/promotions?client_id=${activeDrawer?.id}&tab=composer`);
                      }}
                      className="inline-flex items-center justify-center space-x-1.5 px-3 py-2 text-xs font-semibold rounded-md border transition-colors bg-teal-50 hover:bg-teal-100 dark:bg-teal-950/40 dark:hover:bg-teal-900/50 text-teal-800 dark:text-teal-200 border-teal-300 dark:border-teal-700 shadow-xs"
                      title="Send personalized holiday promotion pitch"
                    >
                      <Megaphone className="w-3.5 h-3.5 text-teal-600 dark:text-teal-300" />
                      <span>Promote</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        if (activeDrawer) handleOpenPortalModal(activeDrawer);
                      }}
                      className="inline-flex items-center justify-center space-x-1.5 px-3 py-2 text-xs font-semibold rounded-md border transition-colors bg-teal-50 hover:bg-teal-100 dark:bg-teal-950/40 dark:hover:bg-teal-900/50 text-teal-800 dark:text-teal-200 border-teal-300 dark:border-teal-700 shadow-xs"
                      title="Generate and manage client onboarding credentials & portal access"
                    >
                      <ShieldCheck className="w-3.5 h-3.5 text-teal-600 dark:text-teal-300" />
                      <span>Portal Hub</span>
                    </button>
                  </div>
                </div>

                {/* Drawer Body (Scrollable) */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Contact & Location Info Grid */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-400 flex items-center space-x-1.5">
                      <User className="w-3.5 h-3.5" />
                      <span>Contact & Location</span>
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 dark:bg-brand-surface/40 p-3.5 rounded-lg border border-slate-200/80 dark:border-brand-border/60 text-xs">
                      <div className="space-y-1">
                        <span className="text-[11px] font-medium text-slate-400">Primary Email</span>
                        <div className="flex items-center space-x-1.5 text-slate-800 dark:text-slate-200 font-medium">
                          <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate">{activeDrawer?.email || '—'}</span>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <span className="text-[11px] font-medium text-slate-400">Secondary / Billing Email</span>
                        <div className="flex items-center space-x-1.5 text-slate-800 dark:text-slate-200">
                          <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate">{activeDrawer?.secondary_email || '—'}</span>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <span className="text-[11px] font-medium text-slate-400">Mobile / WhatsApp</span>
                        <div className="flex items-center space-x-1.5 text-slate-800 dark:text-slate-200">
                          <Smartphone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span>{activeDrawer?.mobile || '—'}</span>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <span className="text-[11px] font-medium text-slate-400">Office Phone</span>
                        <div className="flex items-center space-x-1.5 text-slate-800 dark:text-slate-200">
                          <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span>{activeDrawer?.phone || '—'}</span>
                        </div>
                      </div>

                      <div className="space-y-1 sm:col-span-2">
                        <span className="text-[11px] font-medium text-slate-400">Physical / Billing Address</span>
                        <div className="flex items-start space-x-1.5 text-slate-800 dark:text-slate-200">
                          <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                          <span className="break-words">{activeDrawer?.address || '—'}</span>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <span className="text-[11px] font-medium text-slate-400">Local Timezone</span>
                        <div className="flex items-center space-x-1.5 text-slate-800 dark:text-slate-200">
                          <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span>{activeDrawer?.timezone || '—'}</span>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <span className="text-[11px] font-medium text-slate-400">Preferred Currency</span>
                        <div className="flex items-center space-x-1.5 text-slate-800 dark:text-slate-200">
                          <Coins className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="font-mono font-semibold">{activeDrawer?.currency || 'USD'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Holiday Promotions & Outreach Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-teal-600 dark:text-teal-400 flex items-center space-x-1.5">
                        <Gift className="w-3.5 h-3.5" />
                        <span>Holiday Promotions & Outreach</span>
                      </h3>
                      <button
                        type="button"
                        onClick={() => {
                          handleCloseDrawer();
                          navigate(
                            `/promotions?client_id=${activeDrawer?.id}&holiday_id=${drawerHolidayId}&tab=composer`
                          );
                        }}
                        className="text-[11px] font-semibold text-brand hover:underline flex items-center space-x-1"
                      >
                        <span>Open Studio</span>
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>

                    <div className="p-3.5 rounded-lg border border-teal-200/80 dark:border-teal-900/60 bg-gradient-to-r from-teal-50/70 to-emerald-50/50 dark:from-teal-950/30 dark:to-emerald-950/20 space-y-3">
                      {/* Holiday selector dropdown */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <label className="font-semibold text-slate-800 dark:text-slate-200 flex items-center space-x-1">
                            <span>Pick Holiday / Event:</span>
                          </label>
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              activeDrawerCountdown.isToday
                                ? 'bg-red-500 text-white'
                                : activeDrawerCountdown.daysUntil >= 0 && activeDrawerCountdown.daysUntil <= 7
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                                : 'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300'
                            }`}
                          >
                            {activeDrawerCountdown.badge}
                          </span>
                        </div>
                        <select
                          value={drawerHolidayId}
                          onChange={(e) => setDrawerHolidayId(e.target.value)}
                          className="w-full text-xs bg-white dark:bg-[#12211F] border border-teal-200 dark:border-teal-800 rounded-md p-2 font-medium text-slate-900 dark:text-white"
                        >
                          <optgroup label="🇺🇸 USA Holidays">
                            {ALL_HOLIDAYS.filter((h) => h.region === 'usa').map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.region_label} {h.title} ({h.date})
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="🇪🇺 Europe (UK/EU)">
                            {ALL_HOLIDAYS.filter((h) => h.region === 'europe').map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.region_label} {h.title} ({h.date})
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="🇵🇰 Pakistan Holidays">
                            {ALL_HOLIDAYS.filter((h) => h.region === 'pakistan').map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.region_label} {h.title} ({h.date})
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="🌐 Global Tech Sales">
                            {ALL_HOLIDAYS.filter((h) => h.region === 'global').map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.region_label} {h.title} ({h.date})
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </div>

                      {/* Offer Preview */}
                      {activeDrawerInterpolated && (
                        <div className="p-2.5 rounded-md bg-white/80 dark:bg-[#152724]/80 border border-teal-200/60 dark:border-teal-800/40 space-y-1.5 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-900 dark:text-white">
                              {activeDrawerTemplate.title}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200">
                              {activeDrawerTemplate.suggested_discount || '25%'} Offer
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-600 dark:text-slate-300 line-clamp-2 italic font-mono bg-slate-50 dark:bg-slate-900/50 p-1.5 rounded">
                            {activeDrawerInterpolated.subject}
                          </p>
                        </div>
                      )}

                      {/* 1-Click Action Dispatchers */}
                      <div className="pt-2 border-t border-teal-200/60 dark:border-teal-800/40 grid grid-cols-3 gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={handleDrawerCopyPitch}
                          className="h-8 text-[11px] font-semibold border-teal-200 dark:border-teal-800 hover:bg-teal-50 flex items-center justify-center space-x-1"
                          title="Copy personalized pitch to clipboard"
                        >
                          {drawerCopied ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                              <span className="text-emerald-700">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5 text-teal-700" />
                              <span>Copy Pitch</span>
                            </>
                          )}
                        </Button>

                        <a
                          href={
                            activeDrawer?.email && activeDrawerInterpolated
                              ? buildEmailLink(
                                  activeDrawer.email,
                                  activeDrawerInterpolated.subject,
                                  activeDrawerInterpolated.body
                                )
                              : undefined
                          }
                          className={`inline-flex items-center justify-center space-x-1 h-8 text-[11px] font-semibold rounded-md border transition-colors ${
                            activeDrawer?.email
                              ? 'bg-blue-50/70 hover:bg-blue-100/70 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800'
                              : 'opacity-40 pointer-events-none bg-slate-100 text-slate-400 border-transparent'
                          }`}
                          title={activeDrawer?.email ? `Send prefilled email to ${activeDrawer.email}` : 'No email address'}
                        >
                          <Mail className="w-3.5 h-3.5 text-blue-600" />
                          <span>Email</span>
                        </a>

                        <a
                          href={
                            activeDrawerWhatsApp && activeDrawerInterpolated
                              ? buildWhatsAppLink(
                                  activeDrawerWhatsApp,
                                  `${activeDrawerInterpolated.subject}\n\n${activeDrawerInterpolated.body}`
                                ) || undefined
                              : undefined
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center justify-center space-x-1 h-8 text-[11px] font-semibold rounded-md border transition-colors ${
                            activeDrawerWhatsApp
                              ? 'bg-emerald-50/70 hover:bg-emerald-100/70 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                              : 'opacity-40 pointer-events-none bg-slate-100 text-slate-400 border-transparent'
                          }`}
                          title={activeDrawerWhatsApp ? 'Send prefilled WhatsApp pitch' : 'No mobile number'}
                        >
                          <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                          <span>WhatsApp</span>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Linked Projects Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-400 flex items-center space-x-1.5">
                        <FolderKanban className="w-3.5 h-3.5" />
                        <span>Linked Projects</span>
                      </h3>
                      <Badge variant="secondary" className="text-[10px] font-mono">
                        {activeDrawer?.projects?.length ?? activeDrawer?.active_projects_count ?? activeDrawer?.projects_count ?? 0} total
                      </Badge>
                    </div>

                    {drawerLoading ? (
                      <div className="p-4 text-center text-xs text-slate-400 space-y-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-teal border-t-transparent mx-auto" />
                        <p>Loading linked projects...</p>
                      </div>
                    ) : (activeDrawer?.projects && activeDrawer.projects.length > 0) ? (
                      <div className="space-y-2">
                        {activeDrawer.projects.map((proj) => {
                          const progress = Math.min(100, Math.max(0, Math.round(proj.progress_percent || 0)));
                          return (
                            <div
                              key={proj.id}
                              onClick={() => {
                                handleCloseDrawer();
                                navigate(`/projects/${proj.id || proj.slug}`);
                              }}
                              className="p-3.5 rounded-lg border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] hover:border-brand-teal dark:hover:border-brand-teal transition-all cursor-pointer group shadow-2xs space-y-2"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100 group-hover:text-brand-teal transition-colors flex items-center space-x-1">
                                    <span>{proj.name}</span>
                                    <ArrowRight className="w-3 h-3 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                                  </h4>
                                  {proj.slug && (
                                    <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                                      /{proj.slug}
                                    </p>
                                  )}
                                </div>
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                  {proj.status}
                                </span>
                              </div>

                              {/* Progress bar */}
                              <div className="space-y-1">
                                <div className="flex justify-between items-center text-[10px] text-slate-500">
                                  <span>Progress</span>
                                  <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{progress}%</span>
                                </div>
                                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full transition-all duration-300 ${
                                      progress === 100 ? 'bg-emerald-500' : 'bg-brand-teal'
                                    }`}
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-4 rounded-lg border border-dashed border-slate-200 dark:border-brand-border text-center text-xs text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-brand-surface/20">
                        <FolderKanban className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                        <p>No projects linked to this client yet.</p>
                      </div>
                    )}
                  </div>

                  {/* Billing & Invoices Section (Owner Restricted) */}
                  {isOwner && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-400 flex items-center space-x-1.5">
                          <Receipt className="w-3.5 h-3.5 text-brand-teal" />
                          <span>Billing & Invoices</span>
                        </h3>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            handleCloseDrawer();
                            navigate(`/finance?tab=invoices&action=create_invoice&client_id=${activeDrawer?.id}`);
                          }}
                          className="h-7 text-[11px] font-semibold text-brand-teal border-brand-teal/30 hover:bg-brand-teal/10 flex items-center space-x-1"
                        >
                          <Plus className="w-3 h-3" />
                          <span>New Invoice</span>
                        </Button>
                      </div>

                      {drawerInvoicesLoading ? (
                        <div className="p-4 text-center text-xs text-slate-400 space-y-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-teal border-t-transparent mx-auto" />
                          <p>Loading client billing records...</p>
                        </div>
                      ) : drawerInvoices.length > 0 ? (
                        <div className="space-y-2">
                          {/* Mini Billing Summary */}
                          <div className="grid grid-cols-3 gap-2 bg-slate-50 dark:bg-brand-surface/40 p-2.5 rounded-lg border border-slate-200/80 dark:border-brand-border text-center text-[11px]">
                            <div>
                              <span className="text-slate-400 block text-[10px]">Invoiced</span>
                              <span className="font-mono font-bold text-slate-700 dark:text-slate-200">
                                {drawerInvoices[0]?.currency || (activeDrawer as any)?.currency || 'USD'} {(drawerInvoices.reduce((a, b) => a + (Number(b.total_amount) || 0), 0)).toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[10px]">Paid</span>
                              <span className="font-mono font-bold text-emerald-600">
                                {drawerInvoices[0]?.currency || (activeDrawer as any)?.currency || 'USD'} {(drawerInvoices.reduce((a, b) => a + (Number(b.amount_paid) || 0), 0)).toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[10px]">Balance</span>
                              <span className="font-mono font-bold text-amber-600">
                                {drawerInvoices[0]?.currency || (activeDrawer as any)?.currency || 'USD'} {(drawerInvoices.reduce((a, b) => a + (Number(b.balance_due) || 0), 0)).toLocaleString()}
                              </span>
                            </div>
                          </div>

                          {/* Invoices List */}
                          {drawerInvoices.map((inv) => (
                            <div
                              key={inv.id}
                              onClick={() => {
                                handleCloseDrawer();
                                navigate(`/finance?tab=invoices&search=${inv.invoice_number}`);
                              }}
                              className="p-2.5 rounded-lg border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] hover:border-brand-teal dark:hover:border-brand-teal transition-all cursor-pointer flex items-center justify-between text-xs group"
                            >
                              <div className="space-y-0.5">
                                <div className="font-mono font-bold text-brand-teal flex items-center space-x-1.5">
                                  <span>{inv.invoice_number}</span>
                                  <span
                                    className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold uppercase ${
                                      inv.status === 'paid'
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : inv.status === 'overdue'
                                        ? 'bg-red-50 text-red-700'
                                        : inv.status === 'partial'
                                        ? 'bg-blue-50 text-blue-700'
                                        : 'bg-amber-50 text-amber-700'
                                    }`}
                                  >
                                    {inv.status}
                                  </span>
                                </div>
                                <span className="text-[10px] text-slate-400 font-mono">{inv.issue_date}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-mono font-bold text-slate-800 dark:text-slate-100">
                                  {inv.currency} {Number(inv.total_amount).toLocaleString()}
                                </span>
                                {inv.balance_due > 0 && (
                                  <span className="block text-[10px] font-mono text-amber-600">
                                    Due: {inv.currency} {Number(inv.balance_due).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-3.5 rounded-lg border border-dashed border-slate-200 dark:border-brand-border text-center text-xs text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-brand-surface/20 space-y-1">
                          <Receipt className="w-5 h-5 text-slate-300 mx-auto" />
                          <p>No invoices issued for this client yet.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Internal Notes Section */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-400 flex items-center space-x-1.5">
                      <FileText className="w-3.5 h-3.5" />
                      <span>Internal Notes & Preferences</span>
                    </h3>
                    <div className="p-3.5 rounded-lg border border-slate-200/80 dark:border-brand-border/60 bg-slate-50/70 dark:bg-brand-surface/40 text-xs text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                      {activeDrawer?.notes ? (
                        activeDrawer.notes
                      ) : (
                        <span className="text-slate-400 italic">No internal notes recorded for this client.</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Drawer Footer Actions */}
                <div className="p-4 border-t border-slate-200 dark:border-brand-border bg-slate-50 dark:bg-brand-surface/60 flex items-center justify-between">
                  <div>
                    {isOwner && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClient(activeDrawer!.id, activeDrawer!.name)}
                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center space-x-1.5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Delete Client</span>
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {canManage && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenEdit(activeDrawer!)}
                        className="text-xs flex items-center space-x-1.5"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        <span>Edit Client</span>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={handleCloseDrawer}
                      className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================================
            ADD NEW CLIENT MODAL
           ========================================================================= */}
        {showModal && (
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in"
            onClick={() => setShowModal(false)}
          >
            <div
              className="bg-white dark:bg-[#12211F] rounded-lg shadow-2xl max-w-2xl w-full p-6 space-y-4 animate-in zoom-in-95 border border-slate-200 dark:border-brand-border max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-brand-border pb-3">
                <div>
                  <h2 className="text-lg font-bold text-brand-navy dark:text-brand-ink">Add New Client</h2>
                  <p className="text-xs text-slate-500">Create a comprehensive client profile with contacts, location, and billing info.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleCreateClient} className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Contact / Client Name *</label>
                    <Input
                      required
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                      placeholder="e.g. Sarah Jenkins"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Company / Organization</label>
                    <Input
                      value={newClient.company}
                      onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                      placeholder="e.g. Acme Innovations Corp"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Country & Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Country / Region</label>
                    <Input
                      list="common-countries"
                      value={newClient.country}
                      onChange={(e) => setNewClient({ ...newClient, country: e.target.value })}
                      placeholder="e.g. United States, Germany, Pakistan"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Client Status</label>
                    <select
                      value={newClient.status}
                      onChange={(e) => setNewClient({ ...newClient, status: e.target.value as ClientStatus })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
                    >
                      <option value="active">Active Client</option>
                      <option value="lead">Lead / Prospect</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Platform & Handle */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Platform / Channel</label>
                    <select
                      value={newClient.platform || 'direct'}
                      onChange={(e) => setNewClient({ ...newClient, platform: e.target.value as PlatformType })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
                    >
                      <option value="direct">Direct Client</option>
                      <option value="fiverr">Fiverr</option>
                      <option value="upwork">Upwork</option>
                      <option value="internal">Internal</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Platform Username / Buyer Handle</label>
                    <Input
                      value={newClient.platform_username}
                      onChange={(e) => setNewClient({ ...newClient, platform_username: e.target.value })}
                      placeholder="e.g. dkodaa"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Platform URL */}
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Platform Profile / Workroom URL</label>
                  <Input
                    value={newClient.platform_url}
                    onChange={(e) => setNewClient({ ...newClient, platform_url: e.target.value })}
                    placeholder="https://www.fiverr.com/... or https://www.upwork.com/..."
                    className="mt-1 h-9 text-xs"
                  />
                </div>

                {/* Emails */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Primary Email *</label>
                    <Input
                      required
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      placeholder="client@example.com"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Secondary / Billing Email</label>
                    <Input
                      type="email"
                      value={newClient.secondary_email}
                      onChange={(e) => setNewClient({ ...newClient, secondary_email: e.target.value })}
                      placeholder="billing@example.com"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Phones */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Mobile / WhatsApp</label>
                      {cleanPhoneForWhatsApp(newClient.mobile) && (
                        <a
                          href={`https://wa.me/${cleanPhoneForWhatsApp(newClient.mobile)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center space-x-1"
                          title="Open WhatsApp chat with this number"
                        >
                          <MessageCircle className="w-3 h-3" />
                          <span>Quick WhatsApp</span>
                        </a>
                      )}
                    </div>
                    <Input
                      value={newClient.mobile}
                      onChange={(e) => setNewClient({ ...newClient, mobile: e.target.value })}
                      placeholder="+92 300 1234567"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Office Phone</label>
                    <Input
                      value={newClient.phone}
                      onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                      placeholder="+1 (555) 0192"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Street Address / City / State / Zip</label>
                  <Input
                    value={newClient.address}
                    onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                    placeholder="123 Business Way, Suite 400, Austin, TX 78701"
                    className="mt-1 h-9 text-xs"
                  />
                </div>

                {/* Timezone & Currency */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Client Timezone</label>
                    <Input
                      list="common-timezones"
                      value={newClient.timezone}
                      onChange={(e) => setNewClient({ ...newClient, timezone: e.target.value })}
                      placeholder="e.g. America/New_York (EST)"
                      className="mt-1 h-9 text-xs"
                    />
                    <datalist id="common-timezones">
                      {COMMON_TIMEZONES.map((tz) => (
                        <option key={tz} value={tz} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Preferred Billing Currency</label>
                    <select
                      value={newClient.currency}
                      onChange={(e) => setNewClient({ ...newClient, currency: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
                    >
                      {COMMON_CURRENCIES.map((cur) => (
                        <option key={cur} value={cur}>
                          {cur}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Internal Notes & Preferences</label>
                  <textarea
                    rows={3}
                    value={newClient.notes}
                    onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })}
                    placeholder="Communication preferences, special requirements, NDA status, payment terms..."
                    className="w-full mt-1 p-2.5 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] text-xs text-slate-700 dark:text-slate-200"
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100 dark:border-brand-border">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowModal(false)}
                    className="text-xs"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action"
                  >
                    Create Client
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* =========================================================================
            EDIT CLIENT MODAL
           ========================================================================= */}
        {editingClient && (
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in"
            onClick={() => setEditingClient(null)}
          >
            <div
              className="bg-white dark:bg-[#12211F] rounded-lg shadow-2xl max-w-2xl w-full p-6 space-y-4 animate-in zoom-in-95 border border-slate-200 dark:border-brand-border max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-brand-border pb-3">
                <div>
                  <h2 className="text-lg font-bold text-brand-navy dark:text-brand-ink">Edit Client</h2>
                  <p className="text-xs text-slate-500">Update client attributes, contacts, location, and platform details.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingClient(null)}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSaveEdit} className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Contact / Client Name *</label>
                    <Input
                      required
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Company / Organization</label>
                    <Input
                      value={editForm.company}
                      onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Country & Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Country / Region</label>
                    <Input
                      list="common-countries"
                      value={editForm.country}
                      onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                      placeholder="e.g. United States, Germany, Pakistan"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Client Status</label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value as ClientStatus })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
                    >
                      <option value="active">Active Client</option>
                      <option value="lead">Lead / Prospect</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Platform & Handle */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Platform / Channel</label>
                    <select
                      value={editForm.platform || 'direct'}
                      onChange={(e) => setEditForm({ ...editForm, platform: e.target.value as PlatformType })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
                    >
                      <option value="direct">Direct Client</option>
                      <option value="fiverr">Fiverr</option>
                      <option value="upwork">Upwork</option>
                      <option value="internal">Internal</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Buyer Handle / Username</label>
                    <Input
                      value={editForm.platform_username}
                      onChange={(e) => setEditForm({ ...editForm, platform_username: e.target.value })}
                      placeholder="e.g. dkodaa"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Platform URL */}
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Platform Profile / Workroom URL</label>
                  <Input
                    value={editForm.platform_url}
                    onChange={(e) => setEditForm({ ...editForm, platform_url: e.target.value })}
                    placeholder="https://www.fiverr.com/..."
                    className="mt-1 h-9 text-xs"
                  />
                </div>

                {/* Emails */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Primary Email *</label>
                    <Input
                      required
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Secondary / Billing Email</label>
                    <Input
                      type="email"
                      value={editForm.secondary_email}
                      onChange={(e) => setEditForm({ ...editForm, secondary_email: e.target.value })}
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Phones */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Mobile / WhatsApp</label>
                      {cleanPhoneForWhatsApp(editForm.mobile) && (
                        <a
                          href={`https://wa.me/${cleanPhoneForWhatsApp(editForm.mobile)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center space-x-1"
                          title="Open WhatsApp chat with this number"
                        >
                          <MessageCircle className="w-3 h-3" />
                          <span>Quick WhatsApp</span>
                        </a>
                      )}
                    </div>
                    <Input
                      value={editForm.mobile}
                      onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                      placeholder="+92 300 1234567"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Office Phone</label>
                    <Input
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      placeholder="+1 (555) 0192"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Street Address / City / State / Zip</label>
                  <Input
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    className="mt-1 h-9 text-xs"
                  />
                </div>

                {/* Timezone & Currency */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Client Timezone</label>
                    <Input
                      list="common-timezones"
                      value={editForm.timezone}
                      onChange={(e) => setEditForm({ ...editForm, timezone: e.target.value })}
                      placeholder="e.g. America/New_York (EST)"
                      className="mt-1 h-9 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Preferred Billing Currency</label>
                    <select
                      value={editForm.currency}
                      onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
                    >
                      {COMMON_CURRENCIES.map((cur) => (
                        <option key={cur} value={cur}>
                          {cur}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Internal Notes & Preferences</label>
                  <textarea
                    rows={3}
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    className="w-full mt-1 p-2.5 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] text-xs text-slate-700 dark:text-slate-200"
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100 dark:border-brand-border">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setEditingClient(null)}
                    className="text-xs"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action"
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Client Onboarding & Portal Access Hub Modal */}
        {portalModalClient && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-xl w-full p-6 space-y-5 shadow-2xl text-slate-100 relative my-auto">
              <button
                type="button"
                onClick={() => setPortalModalClient(null)}
                className="absolute top-5 right-5 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="space-y-1">
                <div className="inline-flex items-center space-x-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-teal-500/15 text-teal-300 border border-teal-500/30">
                  <ShieldCheck className="w-3 h-3 text-teal-400" />
                  <span>Client Onboarding & Portal Hub</span>
                </div>
                <h3 className="text-lg font-bold text-white">
                  {portalModalClient.name} {portalModalClient.company ? `(${portalModalClient.company})` : ''}
                </h3>
                <p className="text-xs text-slate-400">
                  Generate 1-click encrypted portal credentials, magic access links, and onboarding invite packages.
                </p>
              </div>

              {/* Credential Generator Form */}
              <div className="p-4 bg-slate-950/70 border border-slate-800/80 rounded-2xl space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-300">Client Email</label>
                    <Input
                      type="email"
                      value={portalCustomEmail}
                      onChange={(e) => setPortalCustomEmail(e.target.value)}
                      placeholder="client@company.com"
                      className="bg-slate-900 border-slate-800 text-white text-xs h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-300">Custom Password (optional)</label>
                    <Input
                      type="text"
                      value={portalCustomPassword}
                      onChange={(e) => setPortalCustomPassword(e.target.value)}
                      placeholder="Auto-generated if empty"
                      className="bg-slate-900 border-slate-800 text-white text-xs h-9"
                    />
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={handleGeneratePortalAccess}
                  disabled={portalGenerating}
                  className="w-full bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs h-9 shadow flex items-center justify-center space-x-1.5"
                >
                  <KeyRound className="w-3.5 h-3.5" />
                  <span>{portalGenerating ? 'Generating Credentials...' : portalAccessData ? 'Reset / Refresh Onboarding Credentials' : 'Generate Onboarding Credentials'}</span>
                </Button>
              </div>

              {/* Generated Credentials & Shareable Invites */}
              {portalAccessData && (
                <div className="space-y-4 pt-1">
                  {/* Credentials Preview Strip */}
                  <div className="p-4 bg-slate-950/80 border border-teal-500/30 rounded-2xl space-y-3 text-xs">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                      <span className="text-[10px] uppercase font-bold text-teal-400">Active Credentials</span>
                      <span className="text-[10px] text-slate-500">
                        Expires in 90 days
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Portal Link</span>
                        <div className="flex items-center space-x-1.5">
                          <span className="font-mono text-teal-300 text-xs truncate max-w-[170px]" title={portalAccessData.portal_url}>
                            {portalAccessData.portal_url}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const fullUrl = `${window.location.origin}${window.location.pathname}${portalAccessData.portal_url}`;
                              navigator.clipboard.writeText(fullUrl);
                              setPortalCopiedField('url');
                              setTimeout(() => setPortalCopiedField(null), 2000);
                            }}
                            className="text-slate-400 hover:text-white p-1"
                            title="Copy Full Portal URL"
                          >
                            {portalCopiedField === 'url' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Generated Password</span>
                        <div className="flex items-center space-x-1.5">
                          <span className="font-mono text-white text-xs">
                            {showPortalPassword ? portalAccessData.password_display : '••••••••••••'}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowPortalPassword(!showPortalPassword)}
                            className="text-slate-400 hover:text-white p-1"
                          >
                            {showPortalPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(portalAccessData.password_display);
                              setPortalCopiedField('pwd');
                              setTimeout(() => setPortalCopiedField(null), 2000);
                            }}
                            className="text-slate-400 hover:text-white p-1"
                            title="Copy Password"
                          >
                            {portalCopiedField === 'pwd' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pre-formatted Invites: WhatsApp & Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* WhatsApp Invite */}
                    <div className="p-3.5 bg-slate-950/60 border border-slate-800 rounded-xl space-y-2 text-xs">
                      <div className="flex items-center space-x-1.5 font-bold text-emerald-400">
                        <MessageCircle className="w-4 h-4" />
                        <span>WhatsApp Invite</span>
                      </div>
                      <p className="text-[11px] text-slate-400">Pre-formatted onboarding message ready for 1-click dispatch.</p>
                      <div className="flex items-center space-x-2 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            const fullUrl = `${window.location.origin}${window.location.pathname}${portalAccessData.portal_url}`;
                            const text = `Hello ${portalModalClient.name}! Welcome to your dedicated TechoSolution Client Portal.\n\nReview your project roadmap, milestones, deliverables, and billing here:\n${fullUrl}\n\nLogin Password: ${portalAccessData.password_display}\n\nFeel free to message us here if you have any questions!`;
                            const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
                            window.open(waUrl, '_blank');
                          }}
                          className="flex-1 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[11px] font-bold text-center transition-colors"
                        >
                          Open WhatsApp
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const fullUrl = `${window.location.origin}${window.location.pathname}${portalAccessData.portal_url}`;
                            const text = `Hello ${portalModalClient.name}! Welcome to your dedicated TechoSolution Client Portal.\n\nReview your project roadmap, milestones, deliverables, and billing here:\n${fullUrl}\n\nLogin Password: ${portalAccessData.password_display}\n\nFeel free to message us here if you have any questions!`;
                            navigator.clipboard.writeText(text);
                            setPortalCopiedField('wa_text');
                            setTimeout(() => setPortalCopiedField(null), 2000);
                          }}
                          className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-[11px]"
                          title="Copy WhatsApp Message"
                        >
                          {portalCopiedField === 'wa_text' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>

                    {/* Email Invite */}
                    <div className="p-3.5 bg-slate-950/60 border border-slate-800 rounded-xl space-y-2 text-xs">
                      <div className="flex items-center space-x-1.5 font-bold text-teal-400">
                        <Mail className="w-4 h-4" />
                        <span>Email Package</span>
                      </div>
                      <p className="text-[11px] text-slate-400">Formal client onboarding welcome email with credentials.</p>
                      <div className="flex items-center space-x-2 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            const fullUrl = `${window.location.origin}${window.location.pathname}${portalAccessData.portal_url}`;
                            const subj = `[TechoSolution] Welcome to Your Client Project Portal — ${portalModalClient.name}`;
                            const body = `Dear ${portalModalClient.name},\n\nWe have set up your dedicated Client Account Portal for tracking your projects, deliverable milestones, and billing statements with TechoSolution.\n\nAccess Your Portal:\n${fullUrl}\n\nAccount Credentials:\nEmail: ${portalAccessData.client_email || portalModalClient.email || ''}\nPassword: ${portalAccessData.password_display}\n\nBest regards,\nTechoSolution Agency Team`;
                            window.location.href = `mailto:${encodeURIComponent(portalAccessData.client_email || portalModalClient.email || '')}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
                          }}
                          className="flex-1 py-1.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-[11px] font-bold text-center transition-colors"
                        >
                          Send Email
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const fullUrl = `${window.location.origin}${window.location.pathname}${portalAccessData.portal_url}`;
                            const body = `Dear ${portalModalClient.name},\n\nWe have set up your dedicated Client Account Portal for tracking your projects, deliverable milestones, and billing statements with TechoSolution.\n\nAccess Your Portal:\n${fullUrl}\n\nAccount Credentials:\nEmail: ${portalAccessData.client_email || portalModalClient.email || ''}\nPassword: ${portalAccessData.password_display}\n\nBest regards,\nTechoSolution Agency Team`;
                            navigator.clipboard.writeText(body);
                            setPortalCopiedField('email_text');
                            setTimeout(() => setPortalCopiedField(null), 2000);
                          }}
                          className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-[11px]"
                          title="Copy Email Text"
                        >
                          {portalCopiedField === 'email_text' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Direct Preview Link */}
                  <div className="pt-2 flex justify-end">
                    <a
                      href={`${window.location.origin}${window.location.pathname}${portalAccessData.portal_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center space-x-1.5 text-xs text-teal-400 hover:text-teal-300 font-bold"
                    >
                      <span>Preview Client Portal View</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Shared Delete Confirmation Dialog */}
        <DeleteConfirmModal
          isOpen={!!deleteClientTarget}
          itemType="Client"
          itemName={deleteClientTarget?.name}
          onClose={() => setDeleteClientTarget(null)}
          onConfirm={confirmDeleteClient}
        />
      </div>
    </PullToRefresh>
  );
}
